import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { LogEntry, WsState, Settings } from '../api'
import { LogStats } from '../useLogStats'
import LogPanel from './LogPanel'
import ConfigSummary from './ConfigSummary'
import ReportPanel from './ReportPanel'
import RunHistory from './RunHistory'
import AuditHistory from './AuditHistory'
import DailyHistoryChart from './DailyHistoryChart'
import CaptchaDetailPanel from './CaptchaDetailPanel'
import { ChevronDown, ChevronUp, Maximize2, Minimize2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

type Tab = 'config' | 'log' | 'report' | 'captcha' | 'history' | 'daily' | 'audit'

interface Props {
  logs: LogEntry[]
  getAllLogs?: () => LogEntry[]
  onClearLogs: () => void
  wsState: WsState
  cfg: Settings
  running: boolean
  stats: LogStats
  mode?: 'booster' | 'profiler'
  campaignId?: string
  actionInProgress?: string | null
  actionProjectName?: string
  elapsedSec?: number
}

const MIN_H = 120
const MAX_H = 800
const DEFAULT_H = 288

function loadPanelState() {
  try {
    const raw = localStorage.getItem('cb-bottom-panel')
    if (raw) return JSON.parse(raw) as Record<string, any>
  } catch { /* ignore */ }
  return null
}

function savePanelState(state: Record<string, any>) {
  try { localStorage.setItem('cb-bottom-panel', JSON.stringify(state)) } catch { /* ignore */ }
}

export default function BottomPanel({ logs, getAllLogs, onClearLogs, wsState, cfg, running, stats, mode = 'booster', campaignId, actionInProgress, actionProjectName, elapsedSec }: Props) {
  const saved = useRef(loadPanelState())
  const [tab, setTab] = useState<Tab>(() => (saved.current?.tab as Tab) || 'log')
  const [collapsed, setCollapsed] = useState(() => saved.current?.collapsed ?? false)
  const [height, setHeight] = useState(() => saved.current?.height ?? DEFAULT_H)
  const [expanded, setExpanded] = useState(() => saved.current?.expanded ?? false)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  const dragging = useRef(false)
  const startY = useRef(0)
  const startH = useRef(0)

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    dragging.current = true
    startY.current = e.clientY
    startH.current = height
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'

    const onMove = (ev: MouseEvent) => {
      const delta = startY.current - ev.clientY
      setHeight(Math.max(MIN_H, Math.min(MAX_H, startH.current + delta)))
    }
    const onUp = () => {
      dragging.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [height])

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    dragging.current = true
    startY.current = e.touches[0].clientY
    startH.current = height
  }, [height])

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!dragging.current) return
    const delta = startY.current - e.touches[0].clientY
    setHeight(Math.max(MIN_H, Math.min(MAX_H, startH.current + delta)))
  }, [])

  const onTouchEnd = useCallback(() => { dragging.current = false }, [])

  const tabs = useMemo(() => {
    // Mode-strict counters: profiler never falls back to booster maps / client
    // log-ring success (that ring under-counts after refresh → badge «0/88»
    // while campaign_runtime already has thousands of OK).
    let ok: number
    let fail: number
    if (mode === 'profiler') {
      const pOkMap = stats.profilerOkGrouped
        ? Object.values(stats.profilerOkGrouped).reduce((a: number, g: any) => a + (Number(g?.count) || 0), 0)
        : 0
      const pFailMap = stats.profilerFailGrouped
        ? Object.values(stats.profilerFailGrouped).reduce((a: number, g: any) => a + (Number(g?.count) || 0), 0)
        : 0
      ok = stats.profilerOkCount ?? pOkMap
      fail = stats.profilerFailCount ?? pFailMap
    } else {
      const bOkMap = stats.boosterOk?.size ? [...stats.boosterOk.values()].reduce((a, b) => a + b, 0) : 0
      const bFailMap = stats.boosterFail?.size ? [...stats.boosterFail.values()].reduce((a, b) => a + b, 0) : 0
      ok = stats.boosterOkCount ?? bOkMap
      fail = stats.boosterFailCount ?? bFailMap
    }
    const total = ok + fail
    const failPct = total > 0 ? (fail / total) * 100 : 0
    let resultBadgeCls = ''
    if (total === 0) resultBadgeCls = ''
    else if (fail === 0) resultBadgeCls = 'bg-emerald-500/90 text-white'
    else if (failPct < 15) resultBadgeCls = 'bg-emerald-500/80 text-white'
    else if (failPct < 50) resultBadgeCls = 'bg-amber-500/85 text-white'
    else resultBadgeCls = 'bg-red-500/85 text-white'

    const capSolved = Number(stats.profilerCaptchaDetail?.solved) || 0

    const list: { id: Tab; label: string; badge?: string; badgeVariant?: 'default' | 'destructive'; badgeClass?: string }[] = [
      { id: 'config', label: 'Конфигурация' },
      { id: 'log', label: 'Лог' },
      {
        id: 'report', label: 'Результаты',
        badge: total > 0 ? `${ok}/${fail}` : undefined,
        badgeClass: resultBadgeCls,
      },
    ]
    // Captcha detail tab — profiler product only (never shown in booster UI).
    if (mode === 'profiler') {
      list.push({
        id: 'captcha',
        label: 'Капча',
        badge: capSolved > 0 ? String(capSolved) : undefined,
        badgeClass: capSolved > 0 ? 'bg-violet-500/85 text-white' : undefined,
      })
    }
    list.push(
      { id: 'history', label: 'История' },
      { id: 'daily', label: 'По дням' },
      { id: 'audit', label: 'Изменения' },
    )
    return list
  }, [stats, mode])

  // If user had «Капча» selected then switched product to booster — bounce to log.
  useEffect(() => {
    if (mode !== 'profiler' && tab === 'captcha') setTab('log')
  }, [mode, tab])

  useEffect(() => {
    savePanelState({ tab, collapsed, height, expanded })
  }, [tab, collapsed, height, expanded])

  const expandedH = typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.65) : 600

  const toggleExpand = useCallback(() => {
    if (expanded) {
      setHeight(DEFAULT_H)
      setExpanded(false)
    } else {
      setHeight(Math.max(expandedH, DEFAULT_H))
      setExpanded(true)
      setCollapsed(false)
    }
  }, [expanded, expandedH])

  // Mobile-only primary button behaviour:
  //   collapsed   → one tap shows the panel expanded to full height
  //   fully open  → tap hides it entirely
  //   partial     → tap expands to full
  const mobilePrimary = () => {
    if (collapsed) { setCollapsed(false); setExpanded(true); return }
    if (!expanded) { setExpanded(true); return }
    setCollapsed(true); setExpanded(false)
  }
  const mobileShowUp = collapsed || !expanded

  // On mobile when fully expanded — cover the whole viewport below the app header
  // so the "100% up to the menu" intent is visually correct.
  const mobileFullExpanded = isMobile && expanded && !collapsed

  return (
    <div
      data-no-magic
      className={`flex flex-col shrink-0 ${collapsed ? 'h-11 min-h-[44px] md:h-10 md:min-h-0' : ''} ${mobileFullExpanded ? 'fixed inset-x-1 bottom-1 z-40 rounded-lg border border-border bg-card shadow-xl' : ''}`}
      style={{
        height: collapsed ? undefined : (mobileFullExpanded ? 'calc(100dvh - 64px)' : height),
        top: mobileFullExpanded ? '60px' : undefined,
        transition: dragging.current ? 'none' : 'height 0.2s ease',
      }}
    >
      {!collapsed && !mobileFullExpanded && (
        <div
          onMouseDown={onDragStart}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className="shrink-0 flex items-center justify-center h-[8px] md:h-[5px] cursor-row-resize group touch-none"
        >
          <div className="w-10 h-[3px] rounded-full bg-border group-hover:bg-muted-foreground transition-colors" />
        </div>
      )}

      <div className="flex items-center shrink-0 flex-nowrap min-w-0 px-2 md:px-3 min-h-11 md:min-h-0 md:h-[40px] gap-2 md:gap-3">
        <div className="flex items-center gap-0.5 shrink-0">
          {/* MOBILE: single toggle — up=expand to full / down=hide */}
          <Button
            variant="ghost"
            size="icon"
            onClick={isMobile ? mobilePrimary : () => { setCollapsed(!collapsed); if (collapsed) setExpanded(false) }}
            className="md:hidden h-11 w-11 min-h-[44px] min-w-[44px] shrink-0 text-foreground/70 hover:text-foreground"
            title={mobileShowUp ? 'Развернуть' : 'Скрыть'}
          >
            {mobileShowUp ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </Button>

          {/* DESKTOP: original two-button setup (collapse + expand-to-50%) */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { setCollapsed(!collapsed); if (collapsed) setExpanded(false) }}
            className="hidden md:inline-flex md:h-7 md:w-7 shrink-0 text-foreground/70 hover:text-foreground"
            title={collapsed ? 'Развернуть' : 'Свернуть'}
          >
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleExpand}
            className={`hidden md:inline-flex md:h-7 md:w-7 shrink-0 transition-colors ${expanded ? 'text-primary hover:text-primary/80' : 'text-foreground/50 hover:text-foreground'}`}
            title={expanded ? 'Уменьшить панель' : 'Расширить до 50%'}
          >
            {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </Button>
        </div>

        <div className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]">
          <div className="inline-flex w-max max-w-none items-center rounded-lg border border-border overflow-hidden">
          {tabs.map((t, i) => {
            const active = tab === t.id && !collapsed
            return (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); setCollapsed(false) }}
                className={`
                  relative shrink-0 h-8 px-2 text-xs font-medium transition-all cursor-pointer whitespace-nowrap md:px-4 md:text-[13px]
                  ${i > 0 ? 'border-l border-border' : ''}
                  ${active
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }
                `}
              >
                {t.label}
                {t.badge && (
                  t.badgeClass ? (
                    <span className={`ml-1.5 inline-flex items-center h-[18px] px-1.5 text-[10px] tabular-nums font-bold rounded-full ${t.badgeClass}`}>
                      {t.badge}
                    </span>
                  ) : (
                    <Badge
                      variant={t.badgeVariant ?? 'secondary'}
                      className="ml-1.5 h-4 px-1 text-[10px] tabular-nums font-semibold"
                    >
                      {t.badge}
                    </Badge>
                  )
                )}
              </button>
            )
          })}
          </div>
        </div>
      </div>

      {!collapsed && (
        <div className="flex-1 min-h-0 relative">
          {actionInProgress && (
            <div className="absolute inset-0 z-10 bg-background/60 backdrop-blur-[2px] flex flex-col items-center justify-center gap-3 animate-in fade-in duration-150">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <div className="flex flex-col items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">
                  {actionInProgress === 'start' ? 'Запуск' : 'Остановка'} {actionProjectName && `«${actionProjectName}»`}...
                </span>
                <div className="flex gap-2">
                  <Skeleton className="h-2 w-16 rounded-full" />
                  <Skeleton className="h-2 w-24 rounded-full" />
                  <Skeleton className="h-2 w-12 rounded-full" />
                </div>
              </div>
            </div>
          )}
          {tab === 'config' && <ConfigSummary cfg={cfg} running={running} mode={mode} campaignId={campaignId} />}
          {tab === 'log' && <LogPanel logs={logs} getAllLogs={getAllLogs} onClear={onClearLogs} wsState={wsState} showDebug={cfg?.['BUGLOGGERFINDER'] === 'debug_check_228'} campaignId={campaignId} mode={mode} />}
          {tab === 'report' && <ReportPanel stats={stats} elapsedSec={elapsedSec} running={running} campaignId={campaignId} mode={mode} />}
          {tab === 'captcha' && mode === 'profiler' && <CaptchaDetailPanel stats={stats} />}
          {tab === 'history' && <RunHistory campaignId={campaignId} mode={mode} />}
          {tab === 'daily' && <DailyHistoryChart campaignId={campaignId} mode={mode} />}
          {tab === 'audit' && <AuditHistory campaignId={campaignId} mode={mode} />}
        </div>
      )}
    </div>
  )
}
